// One way to hand the user a file that works everywhere the app runs:
//   - Capacitor app (iOS/Android): write to the app cache, open the native
//     share sheet (Save to Files, AirDrop, Mail…). Plugins are imported lazily
//     and every failure falls through, so an older shell without them still
//     gets the next-best path instead of a silent nothing.
//   - Phones/tablets in a browser: Web Share with the file (iOS 15+, Android
//     Chrome) — the same share sheet — else a normal download.
//   - Desktop: a normal download (Chrome, Firefox, Safari 10.1+ all honour
//     `download` on a blob URL).
// Returns how it was delivered: 'native-share' | 'web-share' | 'download' | 'opened' | 'cancelled'.
import { Capacitor } from '@capacitor/core';

export function isTouchDevice() {
    const ua = navigator.userAgent || '';
    return /iPhone|iPad|iPod|Android/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1); // iPadOS reports as Mac
}

export function isNativeApp() {
    try { return Capacitor.isNativePlatform(); } catch (e) { return false; }
}

function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(',')[1] || '');
        r.onerror = reject;
        r.readAsDataURL(blob);
    });
}

async function nativeShare(blob, filename, title) {
    const [{ Filesystem, Directory }, { Share }] = await Promise.all([import('@capacitor/filesystem'), import('@capacitor/share')]);
    const data = await blobToBase64(blob);
    const written = await Filesystem.writeFile({ path: filename, data, directory: Directory.Cache });
    await Share.share({ title: title || filename, url: written.uri, dialogTitle: 'Save or share' });
    return 'native-share';
}

async function webShare(blob, filename, title) {
    if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') return null;
    const file = new File([blob], filename, { type: blob.type });
    if (!navigator.canShare({ files: [file] })) return null;
    try {
        await navigator.share({ files: [file], title: title || filename });
        return 'web-share';
    } catch (e) {
        if (e && e.name === 'AbortError') return 'cancelled';
        return null;
    }
}

function anchorDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    return 'download';
}

export async function saveFile({ blob, filename, title }) {
    if (!blob || !filename) throw new Error('saveFile needs a blob and a filename');
    if (isNativeApp()) {
        try { return await nativeShare(blob, filename, title); } catch (e) { console.warn('[fileExport] native share unavailable, falling back', e && e.message); }
    }
    if (isTouchDevice()) {
        const r = await webShare(blob, filename, title);
        if (r) return r;
    }
    try { return anchorDownload(blob, filename); } catch (e) { console.warn('[fileExport] download failed, opening instead', e && e.message); }
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    return 'opened';
}

/** Build a real PDF from tables (used where a print dialog can't be relied on). Lazy-loads jsPDF. */
export async function buildPdf({ title, subtitle, metrics = [], tables = [], footer = 'Powered by Beu' }) {
    const [{ jsPDF }, autoTableMod] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
    const autoTable = autoTableMod.default || autoTableMod.autoTable;
    const widest = tables.reduce((m, t) => Math.max(m, (t.headers || []).length), 0);
    const doc = new jsPDF({ orientation: widest > 7 ? 'landscape' : 'portrait', unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 40;
    let y = margin;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(31, 41, 55);
    doc.text(String(title || 'Report'), margin, y); y += 20;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(107, 114, 128);
    if (subtitle) { doc.text(String(subtitle), margin, y); }
    doc.text(`Generated ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`, pageW - margin, y, { align: 'right' });
    y += 10; doc.setDrawColor(0, 126, 160); doc.setLineWidth(2); doc.line(margin, y, pageW - margin, y); y += 18;
    if (metrics.length) {
        const cellW = (pageW - margin * 2) / metrics.length;
        metrics.forEach((m, i) => {
            const x = margin + cellW * i + cellW / 2;
            doc.setFontSize(8); doc.setTextColor(107, 114, 128); doc.text(String(m.label || '').toUpperCase(), x, y, { align: 'center' });
            doc.setFontSize(13); doc.setTextColor(31, 41, 55); doc.setFont('helvetica', 'bold'); doc.text(String(m.value ?? ''), x, y + 16, { align: 'center' }); doc.setFont('helvetica', 'normal');
        });
        y += 34;
    }
    const cell = (c) => (c && typeof c === 'object' && 'v' in c) ? c.v : c;
    for (const t of tables) {
        if (t.title) { doc.setFontSize(9); doc.setTextColor(107, 114, 128); doc.text(String(t.title).toUpperCase(), margin, y); y += 6; }
        autoTable(doc, {
            startY: y,
            margin: { left: margin, right: margin },
            head: [(t.headers || []).map(h => (typeof h === 'object' ? h.label : h))],
            body: (t.rows || []).map(r => r.map(cell)),
            foot: t.foot ? [t.foot.map(cell)] : undefined,
            styles: { fontSize: 8, cellPadding: 4, textColor: [31, 41, 55], lineColor: [243, 244, 246], lineWidth: 0.5 },
            headStyles: { fillColor: [249, 250, 251], textColor: [107, 114, 128], fontStyle: 'bold' },
            footStyles: { fillColor: [249, 250, 251], textColor: [31, 41, 55], fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [250, 250, 250] },
            columnStyles: (t.headers || []).reduce((o, h, i) => { if (h && typeof h === 'object' && h.align === 'right') o[i] = { halign: 'right' }; return o; }, {}),
            didParseCell: (d) => { if (d.section === 'body') { const raw = (t.rows[d.row.index] || [])[d.column.index]; if (raw && typeof raw === 'object' && raw.bold) d.cell.styles.fontStyle = 'bold'; } }
        });
        y = doc.lastAutoTable.finalY + 18;
    }
    const pages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pages; p++) {
        doc.setPage(p); doc.setFontSize(8); doc.setTextColor(156, 163, 175);
        doc.text(footer, margin, doc.internal.pageSize.getHeight() - 20);
        doc.text(`Confidential · ${p}/${pages}`, pageW - margin, doc.internal.pageSize.getHeight() - 20, { align: 'right' });
    }
    return doc.output('blob');
}
